import * as React from 'react';
import { atom, useAtomValue } from 'jotai';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { WorkerEducationEmbedded } from '@restworld/utility-types';
import { FormLabelWrapper, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';
import { DatePicker } from '@restworld/ui-ds-base';
import { genericDateFormat, toDateFormat } from '@restworld/utils-common';

import {
  EditFieldOptionsModal,
  EditModalFooter,
  EditModalTitle,
  FieldOptionsViewType,
  IEditModalProps,
} from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';

type IEducationFormContentProps = IEditModalProps;
type InputProps = {
  education_title: string;
  issue_date: string;
};
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return workerData.education_em.map((v) => ({
    education_title: v.education_title,
    issue_date: v.issue_date,
  }));
});
export const EducationFormContent: React.FunctionComponent<IEducationFormContentProps> = ({
  onClose,
  editPageTitle,
  mode,
  editIndex,
}) => {
  const { t } = useTranslation();
  const defaultValues = useAtomValue(defaultValuesAtom)[editIndex];
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<InputProps>({
    reValidateMode: 'onSubmit',
    defaultValues: mode === 'edit' ? defaultValues : {},
  });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editIndex, editPageType: 'education' });
  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();
  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    (params) => {
      try {
        const newEducation = {
          ...params,
          ...(params.issue_date ? { issue_date: toDateFormat(params.issue_date, genericDateFormat) } : {}),
        };
        if (mode === 'add') {
          apiSubmitHandler({
            education_em: [
              ...(workerData?.education_em ? workerData.education_em : []),
              ...[newEducation],
            ] as WorkerEducationEmbedded[],
          });
        } else {
          const educations = [...workerData.education_em];
          educations[editIndex] = { ...newEducation } as WorkerEducationEmbedded;
          apiSubmitHandler({ education_em: educations as WorkerEducationEmbedded[] });
        }
      } catch (error) {
        console.error(error, `Error in education form submit`);
      }
    },
    [apiSubmitHandler, editIndex, mode, workerData.education_em]
  );
  const checkIfErrored = (fieldKey: keyof InputProps) => errors && Object.keys(errors).includes(fieldKey);
  const fieldLanguages = workerData.education_em.map((v) => v.education_title);
  const selectedEducation = defaultValues?.education_title;
  const selectedEducations = fieldLanguages?.filter((v) => v !== selectedEducation);
  const educationOptions = [...workerFieldOptionsData.educationTitle].filter((v) => !selectedEducations.includes(v.id));
  return (
    <div className="w-full">
      <EditModalTitle
        onClose={onClose}
        editPageTitle={editPageTitle}
        onDelete={
          mode === 'edit'
            ? () => {
                const inputProps = {
                  education_em: [...workerData.education_em].filter((_, i) => i !== editIndex),
                };
                apiSubmitHandler(inputProps);
              }
            : null
        }
        loading={loading}
      />
      <form action="" method="post" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* <div className="flex flex-col gap-4 md:flex-row"> */}
        <FormLabelWrapper id={`education_title`} isFullWidth label={t('common:qualification_1')}>
          <Controller
            name={`education_title`}
            control={control}
            rules={{ required: mode === 'add' }}
            render={({ field }) => (
              <TextInput
                id={`education_title`}
                placeholder={t('onboarding-page:choose_title_type')}
                type="text"
                rightIcon={ChevronDownIcon}
                helperText={
                  checkIfErrored('education_title') ? (
                    <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                  ) : null
                }
                color={checkIfErrored('education_title') ? 'failure' : undefined}
                readOnly
                {...field}
                value={workerFieldOptionsData?.educationTitle?.find((v) => v.id === field.value)?.label || ''}
                onClick={() => {
                  toggleFieldOptionsView({
                    showOptionsView: true,
                    id: `education_title`,
                    selected: field.value ?? '',
                    options: educationOptions,
                    mainTitle: t('common:training'),
                  });
                }}
              />
            )}
          />
        </FormLabelWrapper>
        <FormLabelWrapper id={'issue_date'} isFullWidth label={t('common:release_date')}>
          <Controller
            name="issue_date"
            control={control}
            rules={{ required: mode === 'add' }}
            render={({ field }) => {
              return (
                <DatePicker
                  id="issue_date"
                  selectRange={false}
                  helperText={checkIfErrored('issue_date') ? t('onboarding-page:this_field_is_required') : null}
                  fieldState={checkIfErrored('issue_date') ? 'error' : 'normal'}
                  locale="it-IT"
                  maxDate={new Date()}
                  {...(field.value ? { value: new Date(field.value) } : {})}
                  {...field}
                />
              );
            }}
          />
        </FormLabelWrapper>
        {/* </div> */}
        <EditModalFooter
          onClose={() => {
            onClose();
            reset(defaultValues);
          }}
          loading={loading}
          editIndex={editIndex}
          editPageType="education"
        />
      </form>
      <EditFieldOptionsModal
        {...fieldOptionsView}
        value={fieldOptionsView.selected ?? (getValues(fieldOptionsView.id) as string[] | string)}
        onChange={(value) => {
          if (typeof value === 'string') {
            toggleFieldOptionsView((p) => ({ ...p, selected: value }));
          }
        }}
        onBack={() => toggleFieldOptionsView(defaultModalFieldOptions)}
        onSave={() => {
          setValue(fieldOptionsView.id, fieldOptionsView.selected);
          toggleFieldOptionsView(defaultModalFieldOptions);
        }}
        visible={fieldOptionsView.showOptionsView}
        returnType="string"
      />
    </div>
  );
};

export default EducationFormContent;
interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id'> {
  id: 'education_title';
  selected: string;
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'education_title',
  returnType: 'string',
  selected: '',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'education_title',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};
